import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import EventCard from "@/src/components/events/event-card";
import EventCardSkeleton from "@/src/components/events/event-card-skeleton";
import EventsEmptyState from "@/src/components/events/events-empty-state";
import CategoryTags from "@/src/components/news/latest-news/category-tags";
import ScButton from "@/src/components/ui-kit/button";
import ScSearchInput from "@/src/components/ui-kit/search-bar";
import ToolbarCreateButton from "@/src/components/ui-kit/toolbar-create-button";
import ScTabs from "@/src/components/ui-kit/tabs";
import { useAuth } from "@/src/contexts/auth.context";
import { usePermissions } from "@/src/hooks/use-permissions";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";
import { EventItem, GetEventsResponse } from "@/src/models/event.model";
import { useGetCategoriesQuery } from "@/src/services/api/category/category.hooks";
import {
  useDeleteEventMutation,
  useGetEventsQuery,
} from "@/src/services/api/events/events.hooks";
import {
  useGetUserArchivedEventIdsQuery,
  useUpdateUserArchivedEventIdsMutation,
} from "@/src/services/api/profile/profile.hooks";
import { useTheme } from "@/src/styles/theme-provider";
import { router } from "expo-router";
import { AddCircle } from "iconsax-react-nativejs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FlatList,
  Image,
  LayoutChangeEvent,
  Platform,
  ScrollView,
  Text,
  View,
} from "react-native";

const EVENT_TABS = [
  "LABEL_TAB_ONGOING",
  "LABEL_TAB_UPCOMING",
  "LABEL_TAB_PAST",
  "LABEL_TAB_MY_EVENTS",
  "LABEL_TAB_ARCHIVES",
] as const;

type EventTab = (typeof EVENT_TABS)[number];

const PAGE_SIZE = 6;
export const GAP = 12;
export const CONTAINER_PADDING = 4;
const EVENTS_SORT = JSON.stringify({ startTime: 1 });

const buildFilter = (
  tab: EventTab,
  search: string,
  currentUserId: string,
  nowIso: string,
  archivedEventIds: string[],
  categoryId: string | null,
): string => {
  const nowDate = { $date: nowIso };

  const tabFilter: Record<string, unknown> = (() => {
    switch (tab) {
      case "LABEL_TAB_ONGOING":
        return {
          startTime: { $lte: nowDate },
          endTime: { $gte: nowDate },
          ItemId: { $nin: archivedEventIds },
        };
      case "LABEL_TAB_UPCOMING":
        return {
          startTime: { $gt: nowDate },
          ItemId: { $nin: archivedEventIds },
        };
      case "LABEL_TAB_PAST":
        return {
          endTime: { $lt: nowDate },
          ItemId: { $nin: archivedEventIds },
        };
      case "LABEL_TAB_MY_EVENTS":
        return { CreatedBy: currentUserId };
      case "LABEL_TAB_ARCHIVES":
        return {
          ItemId: { $in: archivedEventIds },
        };
    }
  })();

  if (search.trim()) {
    tabFilter.title = { $regex: search.trim(), $options: "i" };
  }
  if (categoryId) {
    tabFilter.categoryIds = categoryId;
  }

  return JSON.stringify(tabFilter);
};

const getColumns = (width: number): number => {
  if (width >= 700) return 3;
  if (width >= 400) return 2;
  return 1;
};

const getCardWidth = (containerWidth: number, columns: number): number =>
  (containerWidth - GAP * (columns - 1)) / columns;

const Events = () => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { user } = useAuth();
  const { canCreate, canView } = usePermissions();
  const canCreateEvent = canCreate("event");
  const canViewEvent = canView("event");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<EventTab>(EVENT_TABS[0]);
  const [columns, setColumns] = useState(1);
  const [cardWidth, setCardWidth] = useState(0);

  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );

  const currentUserId = user?.itemId ?? "";
  const sessionStart = useRef(new Date().toISOString()).current;

  const { mutate: deleteEvent, isPending: isDeleting } =
    useDeleteEventMutation();

  const { data: archivedEventIds = [] } =
    useGetUserArchivedEventIdsQuery(currentUserId);

  const filter = useMemo(
    () =>
      buildFilter(
        activeTab,
        search,
        currentUserId,
        sessionStart,
        archivedEventIds,
        selectedCategoryId,
      ),
    [
      activeTab,
      search,
      sessionStart,
      archivedEventIds,
      currentUserId,
      selectedCategoryId,
    ],
  );

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
  } = useGetEventsQuery({
    filter,
    sort: EVENTS_SORT,
    pageSize: PAGE_SIZE,
  });

  const { mutate: updateUserProfile } = useUpdateUserArchivedEventIdsMutation();

  const allEvents = useMemo(
    () =>
      data?.pages.flatMap(
        (page: GetEventsResponse["getevents"]) => page.items,
      ) ?? [],
    [data?.pages],
  );

  const { data: categoriesData } = useGetCategoriesQuery({
    pageSize: 1000,
    tag: "events",
  });

  const categories = categoriesData?.items ?? [];

  const visibleCategoryNames = useMemo(
    () => [...new Set(categories.map((cat) => cat.categoryName))],
    [categories],
  );

  const handleEditEvent = useCallback((event: EventItem) => {
    router.push({
      pathname: "/events/[id]/edit",
      params: { id: event.ItemId },
    });
  }, []);

  const handleArchiveEvent = useCallback(
    (event: EventItem) => {
      const isArchived = archivedEventIds.includes(event.ItemId);
      const updated = isArchived
        ? archivedEventIds.filter((id: string) => id !== event.ItemId)
        : [...archivedEventIds, event.ItemId];
      updateUserProfile({
        CreatedBy: currentUserId,
        archivedEventIds: updated,
      });
    },
    [archivedEventIds, currentUserId, updateUserProfile],
  );

  const handleTabChange = useCallback(
    (tab: EventTab) => {
      if (tab === activeTab) return;
      setActiveTab(tab);
      setSelectedCategoryId(null);
    },
    [activeTab],
  );

  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
  }, []);

  const handleEndReached = useCallback(() => {
    if (!isFetching && hasNextPage) fetchNextPage();
  }, [isFetching, hasNextPage, fetchNextPage]);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width - CONTAINER_PADDING;
    const cols = getColumns(width);
    setColumns(cols);
    setCardWidth(getCardWidth(width, cols));
  }, []);

  const handleCategoryPress = useCallback(
    (categoryName: string) => {
      if (categoryName === "__all__") {
        setSelectedCategoryId(null);
        return;
      }
      const matched = categories.find(
        (cat) => cat.categoryName === categoryName,
      );
      setSelectedCategoryId(matched?.ItemId ?? null);
    },
    [categories],
  );
  const handleEventPress = useCallback((event: EventItem) => {
    router.push(`/events/${event.ItemId}`);
  }, []);

  const showJoin = useMemo(
    () => activeTab === "LABEL_TAB_UPCOMING",
    [activeTab],
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!eventToDelete) return;
    deleteEvent(eventToDelete.ItemId, {
      onSettled: () => setEventToDelete(null),
    });
  }, [eventToDelete, deleteEvent]);

  const renderItem = useCallback(
    ({ item: event }: { item: EventItem }) => (
      <View style={{ width: columns > 1 ? cardWidth : "100%" }}>
        <EventCard
          event={event}
          showJoin={showJoin && event.registrationPolicy === "Required"}
          onPress={handleEventPress}
          onEdit={handleEditEvent}
          onDelete={setEventToDelete}
          onArchive={handleArchiveEvent}
          isArchived={archivedEventIds.includes(event.ItemId)}
          isOwner={event.CreatedBy === currentUserId}
        />
      </View>
    ),
    [
      cardWidth,
      columns,
      showJoin,
      handleEditEvent,
      archivedEventIds,
      handleArchiveEvent,
      currentUserId,
      handleEventPress,
    ],
  );

  const renderFooter = useCallback(() => {
    if (!isFetchingNextPage) return null;
    return (
      <>
        {Array.from({ length: columns }).map((_, idx) => (
          <View key={idx} style={{ width: cardWidth }}>
            <EventCardSkeleton />
          </View>
        ))}
      </>
    );
  }, [isFetchingNextPage, columns, cardWidth]);
  /* Toolbar + tabs (single card on web, divided by a full-width line) */
  const toolbar = (
    <View className="mb-4 md:border md:border-ash-3 md:bg-chalk md:rounded-2xl">
      <View className="flex-row items-center justify-between gap-4 pb-3 md:border-b md:border-ash-3 md:p-4">
        <View className="flex-1 md:flex-none md:w-[50%]">
          <ScSearchInput
            containerClassName="rounded-full"
            surface="screen"
            placeholderKey="LABEL_SEARCH_EVENTS"
            value={search}
            onChangeText={handleSearchChange}
            onCreatePress={
              canCreateEvent ? () => router.push("/events/creation") : undefined
            }
          />
        </View>
        {canCreateEvent && (
          <ToolbarCreateButton
            text="BTN_CREATE"
            onPress={() => router.push("/events/creation")}
            hideOnMobile
          />
        )}
      </View>
      <View className="mt-1 justify-center pb-2 md:mt-4 md:px-4 md:pb-4">
        <ScTabs tabs={EVENT_TABS} active={activeTab} onChange={handleTabChange} />
      </View>
    </View>
  );

  const categoryRow = (
    <View className="pb-2">
      {categories.length > 0 && (
        <CategoryTags
          key={activeTab}
          categories={visibleCategoryNames}
          onCategoryPress={handleCategoryPress}
        />
      )}
    </View>
  );

  const loadingGrid = (
    <View className="flex-row flex-wrap" style={{ gap: GAP }}>
      {Array.from({ length: PAGE_SIZE }).map((_, idx) => (
        <View key={idx} style={{ width: columns > 1 ? cardWidth : "100%" }}>
          <EventCardSkeleton />
        </View>
      ))}
    </View>
  );

  const errorView = (
    <View className="items-center py-6 gap-2">
      <Image
        source={require("@/src/assets/images/error.png")}
        style={{ resizeMode: "contain", width: 300, height: 300 }}
        className="w-16 h-16 rounded-full"
        alt="error.img"
      />
      <Text className="text-gray-dark text-sm">{t("ERROR_LOADING_EVENTS")}</Text>
      <ScButton mode="outlined" text="BTN_RETRY" onPress={() => refetch()} />
    </View>
  );

  const confirmModal = (
    <ConfirmModal
      visible={!!eventToDelete}
      title="LABEL_DELETE_EVENT"
      description="QUEST_DELETE_EVENT"
      confirmText={t("BTN_DELETE")}
      cancelText={t("BTN_CANCEL")}
      loading={isDeleting}
      onConfirm={handleDeleteConfirm}
      onCancel={() => setEventToDelete(null)}
    />
  );

  // Web: keep the existing ScrollView layout (no nesting error on web).
  if (Platform.OS === "web") {
    return (
      <View className="flex-1 w-full bg-obsidian">
        <ScrollView
          className="flex-1"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ flexGrow: 1 }}
        >
          {toolbar}
          <View className="flex-1 md:rounded-2xl md:border md:border-ash-3 md:bg-chalk md:p-4">
            {categoryRow}
            {isLoading ? (
              loadingGrid
            ) : isError ? (
              errorView
            ) : (
              <FlatList
                onLayout={handleLayout}
                key={columns}
                data={allEvents}
                keyExtractor={(event) => event.ItemId}
                numColumns={columns}
                columnWrapperStyle={columns > 1 ? { gap: GAP } : undefined}
                contentContainerStyle={{ gap: GAP, flexGrow: 1 }}
                ListEmptyComponent={<EventsEmptyState />}
                ListFooterComponent={renderFooter}
                onEndReachedThreshold={0.3}
                onEndReached={handleEndReached}
                showsVerticalScrollIndicator={false}
                renderItem={renderItem}
              />
            )}
          </View>
          {confirmModal}
        </ScrollView>
      </View>
    );
  }

  // Native: FlatList is the scroll container (toolbar fixed above it) so a
  // VirtualizedList is never nested inside a ScrollView of the same orientation.
  if (!canViewEvent) {
    return (
      <View className="flex-1 w-full bg-obsidian">
        <PermissionDenied />
      </View>
    );
  }

  return (
    <View className="flex-1 w-full bg-obsidian">
      {toolbar}
      <FlatList
        onLayout={handleLayout}
        key={columns}
        data={isLoading || isError ? [] : allEvents}
        keyExtractor={(event) => event.ItemId}
        numColumns={columns}
        columnWrapperStyle={columns > 1 ? { gap: GAP } : undefined}
        contentContainerStyle={{ gap: GAP, flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
        ListHeaderComponent={
          <View>
            {categoryRow}
            {isLoading ? loadingGrid : null}
            {isError ? errorView : null}
          </View>
        }
        ListEmptyComponent={
          !isLoading && !isError ? <EventsEmptyState /> : null
        }
        ListFooterComponent={renderFooter}
        onEndReachedThreshold={0.3}
        onEndReached={handleEndReached}
        renderItem={renderItem}
      />
      {confirmModal}
    </View>
  );
};

export default Events;
